import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validator, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil, tap } from 'rxjs/operators';
import { DropdownItemModel, DropdownMultiselectSettings, Layouts } from 'ng-voxloud';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import { environment } from 'src/environments/environment';
import { DealIdExplainationPopupComponent } from '../deal-id-explaination-popup/deal-id-explaination-popup.component';
import { ExpireModel, PAYMENT_METHODS_LIST, Plan, WIFI_VALUES } from 'src/app/domains/sales/activation-link/models/activation-link.model';
import { ActivationLinkService } from 'src/app/domains/sales/activation-link/services';
import { ActivateFormValue } from '../../models/activate-link.model';
import { DISCOUNT_AMOUNT_LIST, EXPIRES_TIME_LIST } from '../../constant/activation-link.constant';

@Component({
  selector: 'gdp-activation-link-form',
  templateUrl: './activation-link-form.component.html',
  styleUrls: ['./activation-link-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ActivationLinkFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ActivationLinkFormComponent),
      multi: true
    }
  ]
})
export class ActivationLinkFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  public form: UntypedFormGroup;
  public countries = environment.servedCountries;
  public expiresList: ExpireModel[] = EXPIRES_TIME_LIST;
  public wifiList: string[] = WIFI_VALUES;
  public isWifiDisabled: boolean = false;
  public paymentMethodsList: string[] = PAYMENT_METHODS_LIST;
  public discountAmountList: number[] = DISCOUNT_AMOUNT_LIST;
  // Plans variables
  public _loading = {
    getPlans: false
  };
  public filteredPlans: Plan[];
  private plans: Plan[];
  private destroy$ = new Subject<void>();

  // Dropdown multiselect
  public dense: boolean = true;
  public dropdownDisabled: boolean = false;
  public layout: Layouts = Layouts.rows;
  public placeholder: string;
  public dropdownSettings: DropdownMultiselectSettings = {
    singleSelection: false,
    allowSearchFilter: true,
    itemsShowLimit: undefined
  };
  public dropdownList: DropdownItemModel[] = [];
  public language$: Observable<string> = this.translateService.onLangChange.pipe(
    map((e: LangChangeEvent) => e.lang),
    startWith(this.translateService.currentLang),
    tap(() => { this.setPlansDropdown() }),
    takeUntil(this.destroy$)
  )

  @Input() allowDiscount: boolean = true;
  @Input() allowWifi: boolean = true;
  @Input() loadingExternal: boolean = false;

  // ControlValueAccessor callbacks
  private onChange: (value: ActivateFormValue) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private fb: UntypedFormBuilder,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private activationLinkService: ActivationLinkService
  ) { }

  ngOnInit(): void {
    this.getPlans();
    this.form = this.fb.group({
      name: new FormControl<string>('', [Validators.required]),
      country: new FormControl<{ iso2: string }>(null),
      deal_id: new FormControl<string>(''),
      expires: new FormControl<{ unit: string; value: number; }>(this.expiresList[0], Validators.required),
      sms: new FormControl<boolean>(true, [Validators.required]),
      wifi: new FormControl<string>(this.wifiList[0]),
      paymentMethod: new FormControl<string>(this.paymentMethodsList[2]),
      discount: new FormControl<boolean>(false)
    });

    this.form.controls.country.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (country: { iso2: string }) => {
          if (!country?.iso2) return;
          this.filterPlans('v4', `${country.iso2.toLowerCase()}-`);
          if (this.form.controls.country.value.iso2 === 'IT') {
            this.isWifiDisabled = false;
          } else {
            this.form.controls.wifi.patchValue(this.wifiList[0]);
            this.isWifiDisabled = true;
          }
        });

    // Subscribe to form value changes to notify parent
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        setTimeout(() => this.onChange(value), 0);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: ActivateFormValue): void {
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: ActivateFormValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { invalidForm: true };
  }

  public getFormControl(name: string) {
    return this.form.controls[name];
  }

  public isControlInvalid(name: string): boolean {
    return this.getFormControl(name).invalid && this.getFormControl(name).dirty;
  }

  public openDealIdModal(): void {
    this.modalService.open(DealIdExplainationPopupComponent, { size: 'lg' });
  }

  public selectExpires(expires: ExpireModel): void {
    this.form.patchValue({ expires });
  }

  public selectWifi(wifi: string): void {
    this.form.patchValue({ wifi });
  }

  public selectPaymentMethod(paymentMethod: string): void {
    this.form.patchValue({ paymentMethod });
  }

  public setApplyDiscount(): void {
    const control = this.getFormControl('discount');
    control.setValue(!control.value);
    if (control.value) {
      this.setPlansDropdown();
      this.form.addControl('amount', new FormControl<number>(this.discountAmountList[0]));
      this.form.addControl('duration', new FormControl<number>(1));
      this.form.addControl('durationForever', new FormControl<boolean>(false));
      this.form.addControl('plans', new FormControl<Plan[]>(null, Validators.required));
    } else {
      // setTimeout is because we need sometime before fire gdp-quantity-selector component
      setTimeout(() => {
        this.form.removeControl('amount');
        this.form.removeControl('duration');
        this.form.removeControl('durationForever');
        this.form.removeControl('plans');
      }, 100);
    }
  }

  public selectAmount(amount: number): void {
    this.form.patchValue({ amount });
  }

  private getPlans(): void {
    this._loading.getPlans = true;
    this.activationLinkService.getAllPlans().subscribe((res: Plan[]) => {
      this.plans = res;
      this.filterPlans('v4', `${this.countries[0].iso2.toLowerCase()}-`);
      this._loading.getPlans = false;
    }, () => {
      this._loading.getPlans = false;
    });
  }

  private setPlansDropdown(): void {
    this.placeholder = this.translateService.instant("sales.activation-link.form.plans.placeholder");
    this.dropdownSettings = {
      ...this.dropdownSettings,
      selectAllText: this.translateService.instant('sales.activation-link.form.plans.select-all'),
      unSelectAllText: this.translateService.instant('sales.activation-link.form.plans.unselect-all'),
      searchPlaceholderText: this.translateService.instant('sales.activation-link.form.plans.search-placeholder'),
      noDataAvailablePlaceholderText: this.translateService.instant('sales.activation-link.form.plans.empty')
    }
  }

  // Methods to filter plans
  private filterPlans(version: string, country: string): void {
    this.filteredPlans = this.plans.filter(plan =>
      plan.id.includes(version)
      && plan.id.includes(country)
      && !plan.id.includes('partner') // we want only direct activation plans
      && !plan.id.includes('biennal') // we don't want biennal plans for direct activation customers
    );
    this.fillDropdownList();
  }

  private fillDropdownList(): void {
    this.dropdownList = this.filteredPlans.map((plan, index) => {
      let item: DropdownItemModel = { item_id: index.toString(), item_name: plan.name, data: plan };
      return item;
    });
  }
}
